package org.multiplatform.kotfolio.pages.kmm

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em

@Page("/kmm/multiplatform_settings")
@Composable
fun KMPMultiplatformSettingsPage() {
    BlogLayout(
        PageProperties(
            "multiplatform-settings",
            ""
        ),
        BlogProperties("Kotlin Multiplatform", "multiplatform-settings")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            KotlinCode(
                code = """
                        implementation("com.russhwolf:multiplatform-settings:1.1.1")
                        implementation("com.russhwolf:multiplatform-settings-no-arg:1.1.1")
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    /**
                     * [Scope] denotes the scope or mapping of the shared preference key / worker unique name.
                     *
                     * A shared preference key-value pair can be scoped to :
                     * - [Individual] : value will be same for all Businesses of the User (Individual).
                     * - [Business] : value will be different for each Business of the User (Individual).
                     * Note : to change the scope of an existing key, a [SharedPreferencesMigration] is required.
                     *
                     * A worker can be scoped to :
                     * - [Individual] : no changes in unique worker name.
                     * - [Business] : business id will be appended to the unique worker name.
                     */
                    sealed class Scope {
                        object Individual : Scope()
                        class Business(val businessId: String) : Scope()

                        companion object {
                            private const val DIVIDER = ":::::"

                            fun getScopedKey(key: String, scope: Scope): String {
                                return when (scope) {
                                    is Individual -> key // key (no change)
                                    is Business -> "" // business-id::key (prefix businessId & divider)
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @file:OptIn(ExperimentalSettingsApi::class)

                    import com.russhwolf.settings.ExperimentalSettingsApi
                    import com.russhwolf.settings.coroutines.FlowSettings
                    import kotlinx.coroutines.CoroutineDispatcher
                    import kotlinx.coroutines.flow.Flow
                    import kotlinx.coroutines.flow.emitAll
                    import kotlinx.coroutines.flow.flow
                    import kotlinx.coroutines.flow.flowOn
                    import kotlinx.coroutines.flow.map
                    import kotlinx.coroutines.withContext

                    open class BasePreferences constructor(
                        private val prefs: Lazy<FlowSettings>,
                        private val coroutineDispatcher: CoroutineDispatcher = appDispatchers.io,
                    ) {

                        fun getString(key: String, scope: Scope, defaultValue: String = ""): Flow<String> {
                            return flow {
                                val scopedKey = getScopedKey(key, scope)
                                emitAll(prefs.value.getStringFlow(scopedKey, defaultValue))
                            }.flowOn(coroutineDispatcher)
                        }

                        fun getBoolean(key: String, scope: Scope, defaultValue: Boolean = false): Flow<Boolean> {
                            return flow {
                                val scopedKey = getScopedKey(key, scope)
                                emitAll(prefs.value.getBooleanFlow(scopedKey, defaultValue))
                            }.flowOn(coroutineDispatcher)
                        }

                        fun getInt(key: String, scope: Scope, defaultValue: Int = 0): Flow<Int> {
                            return flow {
                                val scopedKey = getScopedKey(key, scope)
                                emitAll(prefs.value.getIntFlow(scopedKey, defaultValue))
                            }.flowOn(coroutineDispatcher)
                        }

                        fun getLong(key: String, scope: Scope, defaultValue: Long = 0L): Flow<Long> {
                            return flow {
                                val scopedKey = getScopedKey(key, scope)
                                emitAll(prefs.value.getLongFlow(scopedKey, defaultValue))
                            }.flowOn(coroutineDispatcher)
                        }

                        fun getFloat(key: String, scope: Scope, defaultValue: Float = 0f): Flow<Float> {
                            return flow {
                                val scopedKey = getScopedKey(key, scope)
                                emitAll(prefs.value.getFloatFlow(scopedKey, defaultValue))
                            }.flowOn(coroutineDispatcher)
                        }

                        fun <T : Any> getObject(
                            key: String,
                            scope: Scope,
                            defaultValue: T?,
                            converter: Converter<T>,
                        ): Flow<T?> {
                            return flow {
                                val scopedKey = getScopedKey(key, scope)
                                emitAll(
                                    prefs.value.getStringFlow(
                                        key = scopedKey,
                                        defaultValue = defaultValue?.let { converter.serialize(it) } ?: "",
                                    ).map {
                                        if (it.isEmpty()) {
                                            defaultValue
                                        } else {
                                            converter.deserialize(it)
                                        }
                                    },
                                )
                            }.flowOn(coroutineDispatcher)
                        }

                        //endregion

                        //region Setters

                        suspend fun set(key: String, value: String, scope: Scope) = withContext(coroutineDispatcher) {
                            val scopedKey = getScopedKey(key, scope)
                            prefs.value.putString(scopedKey, value)
                        }

                        suspend fun set(key: String, value: Boolean, scope: Scope) = withContext(coroutineDispatcher) {
                            val scopedKey = getScopedKey(key, scope)
                            prefs.value.putBoolean(scopedKey, value)
                        }

                        suspend fun set(key: String, value: Int, scope: Scope) = withContext(coroutineDispatcher) {
                            val scopedKey = getScopedKey(key, scope)
                            prefs.value.putInt(scopedKey, value)
                        }

                        suspend fun set(key: String, value: Long, scope: Scope) = withContext(coroutineDispatcher) {
                            val scopedKey = getScopedKey(key, scope)
                            prefs.value.putLong(scopedKey, value)
                        }

                        suspend fun set(key: String, value: Float, scope: Scope) = withContext(coroutineDispatcher) {
                            val scopedKey = getScopedKey(key, scope)
                            prefs.value.putFloat(scopedKey, value)
                        }

                        suspend fun <T : Any> set(key: String, value: T, scope: Scope, converter: Converter<T>) =
                            withContext(coroutineDispatcher) {
                                val scopedKey = getScopedKey(key, scope)
                                prefs.value.putString(scopedKey, converter.serialize(value))
                            }

                        //endregion

                        suspend fun contains(key: String, scope: Scope): Boolean = withContext(coroutineDispatcher) {
                            val scopedKey = getScopedKey(key, scope)
                            return@withContext prefs.value.hasKey(scopedKey)
                        }

                        suspend fun remove(key: String, scope: Scope) = withContext(coroutineDispatcher) {
                            val scopedKey = getScopedKey(key, scope)
                            prefs.value.remove(scopedKey)
                        }

                        // preserve shared preference version and clear data
                        @OptIn(ExperimentalSettingsApi::class)
                        suspend fun clear() = withContext(coroutineDispatcher) {
                            prefs.value.clear()
                        }
                    }
                    
                    interface Converter<T> {
                        /**
                         * Deserialize to an instance of `T`. The input is retrieved from [Preferences.getString].
                         */
                        fun deserialize(serialized: String): T

                        /**
                         * Serialize the `value` to a String. The result will be used with [Preferences.putString].
                         */
                        fun serialize(value: T): String
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    import kotlinx.coroutines.CoroutineDispatcher

                    interface AppDispatchers {
                        val main: CoroutineDispatcher
                        val io: CoroutineDispatcher
                        val unconfined: CoroutineDispatcher
                    }

                    expect val appDispatchers: AppDispatchers
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    import com.russhwolf.settings.coroutines.FlowSettings

                    interface IdentitySettingsFactory {
                        fun create(): FlowSettings
                    }

                    const val PREF_NAME = "identity_prefs"
                    
                    //androidMain
                    import android.content.Context
                    import com.russhwolf.settings.SharedPreferencesSettings
                    import com.russhwolf.settings.coroutines.FlowSettings
                    import com.russhwolf.settings.coroutines.toFlowSettings
                    import me.tatarka.inject.annotations.Inject

                    @Inject
                    class AndroidIdentitySettingsFactory(private val context: Context, private val ioDispatcher: IoDispatcher) :
                        IdentitySettingsFactory {

                        override fun create(): FlowSettings {
                            return SharedPreferencesSettings.Factory(context).create(PREF_NAME)
                                .toFlowSettings(ioDispatcher)
                        }
                    }
                    
                    import me.tatarka.inject.annotations.Provides

                    interface AndroidIdentityComponent : IdentityComponent {
                        @Provides
                        fun AndroidIdentitySettingsFactory.binds(): IdentitySettingsFactory {
                            return this
                        }
                    }
                    
                    //iosMain
                    import com.russhwolf.settings.NSUserDefaultsSettings
                    import com.russhwolf.settings.coroutines.FlowSettings
                    import com.russhwolf.settings.coroutines.toFlowSettings
                    import me.tatarka.inject.annotations.Inject

                    @Inject
                    class IosIdentitySettingsFactory(private val ioDispatcher: IoDispatcher) : IdentitySettingsFactory {

                        override fun create(): FlowSettings {
                            return NSUserDefaultsSettings.Factory().create(PREF_NAME).toFlowSettings(ioDispatcher)
                        }
                    }
                    
                    import me.tatarka.inject.annotations.Provides

                    interface IosIdentityComponent : IdentityComponent {
                        @Provides
                        fun IosIdentitySettingsFactory.binds(): IdentitySettingsFactory {
                            return this
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    import me.tatarka.inject.annotations.Inject
                    
                    @Inject
                    class IdentityPreferences(
                        settingsFactory: IdentitySettingsFactory,
                        ioDispatcher: IoDispatcher,
                    ) : BasePreferences(lazy { settingsFactory.create() }, ioDispatcher)
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    typealias IoDispatcher = CoroutineDispatcher
                    typealias MainDispatcher = CoroutineDispatcher
                    
                    typealias BaseUrl = String
                    typealias AppVersion = String
                    typealias AppVersionCode = Int
                    typealias Debug = Boolean
                    typealias Flavor = String

                    @Singleton
                    interface BaseComponent {

                        @Singleton
                        @Provides
                        fun provideKtorfit(
                            baseUrl: BaseUrl,
                            appVersion: AppVersion,
                            debug: Debug,
                            clientConfigs: Set<ClientConfig>,
                        ): Ktorfit = createKtorFit(
                            baseUrl = baseUrl,
                            appVersion = appVersion,
                            debug = debug,
                            clientConfigs = clientConfigs,
                        )

                        @Singleton
                        @Provides
                        fun ioDispatcher(): IoDispatcher = appDispatchers.io

                        @Singleton
                        @Provides
                        fun mainDispatcher(): MainDispatcher = appDispatchers.main
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    import kotlinx.coroutines.CoroutineDispatcher

                    interface AppDispatchers {
                        val main: CoroutineDispatcher
                        val io: CoroutineDispatcher
                        val unconfined: CoroutineDispatcher
                    }

                    expect val appDispatchers: AppDispatchers
                    
                    //androidMain
                    import kotlinx.coroutines.CoroutineDispatcher
                    import kotlinx.coroutines.Dispatchers

                    actual val appDispatchers: AppDispatchers = object : AppDispatchers {
                        override val main: CoroutineDispatcher = Dispatchers.Main.immediate
                        override val io: CoroutineDispatcher = Dispatchers.IO
                        override val unconfined: CoroutineDispatcher = Dispatchers.Unconfined
                    }
                    
                    //iosMain
                    import kotlinx.coroutines.CoroutineDispatcher
                    import kotlinx.coroutines.Dispatchers

                    actual val appDispatchers: AppDispatchers = object : AppDispatchers {
                        override val main: CoroutineDispatcher = Dispatchers.Main
                        override val io: CoroutineDispatcher = Dispatchers.Default
                        override val unconfined: CoroutineDispatcher = Dispatchers.Unconfined
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Inject
                    class IdentityLocalSource constructor(
                        private val preferences: Lazy<IdentityPreferences>
                    )     
                    """.trimIndent()
            )

            SpanText(
                text = "ObservableSettings using",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //https://github.com/Evylon/HamsterList-Native (Code Formatting detect)
                        
                    class UserRepository(
                        private val settings: ObservableSettings
                    ) {
                        @OptIn(ExperimentalSettingsApi::class)
                        @NativeCoroutinesState
                        val username: StateFlow<String?> = settings.getStringOrNullStateFlow(
                            coroutineScope = CoroutineScope(Dispatchers.IO),
                            key = SettingsKeys.USERNAME.name
                        )

                        fun setUsername(newName: String) {
                            settings[SettingsKeys.USERNAME.name] = newName.trim().takeIf { it.isNotBlank() }
                        }
                    }
                    
                    enum class SettingsKeys {
                        USERNAME,
                        CURRENT_LIST_ID,
                        SERVER_HOST_NAME
                    }
                    
                    class HomeViewModel(
                        private val settings: ObservableSettings
                    ) : BaseViewModel() {

                        @OptIn(ExperimentalSettingsApi::class)
                        private val currentListId = settings.getStringOrNullStateFlow(
                            coroutineScope = CoroutineScope(Dispatchers.IO),
                            key = SettingsKeys.CURRENT_LIST_ID.name
                        )
                    }
                    
                    @Suppress("EXPECT_ACTUAL_CLASSIFIERS_ARE_IN_BETA_WARNING")
                    expect open class BaseViewModel() {
                        val scope: CoroutineScope
                    }
                    
                    //iosMain
                    import kotlinx.coroutines.CoroutineScope
                    import kotlinx.coroutines.Dispatchers
                    import kotlinx.coroutines.SupervisorJob

                    @Suppress("EXPECT_ACTUAL_CLASSIFIERS_ARE_IN_BETA_WARNING")
                    actual open class BaseViewModel {
                        actual val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
                    }
                    
                    //androidMain
                    import androidx.lifecycle.ViewModel
                    import androidx.lifecycle.viewModelScope

                    @Suppress("EXPECT_ACTUAL_CLASSIFIERS_ARE_IN_BETA_WARNING")
                    actual open class BaseViewModel : ViewModel() {
                        actual val scope = viewModelScope
                    }
                    
                    @OptIn(ExperimentalSettingsApi::class)
                    fun hamsterListModules() = viewModelModule + module {
                        single<ObservableSettings> { Settings().makeObservable() }
                    }

                    """.trimIndent()
            )

            //https://github.com/Junaed29/VendingMachineMultiplatform
            KotlinCode(
                code = """
                        //common
                        import com.russhwolf.settings.Settings
                        /**
                         * A factory interface for creating platform-specific Settings implementations
                         */
                        interface SettingsFactory {
                            fun createSettings(): Settings
                        }

                        /**
                         * Get platform-specific settings implementation
                         */
                        expect fun getSettingsFactory(): SettingsFactory
                        
                        //StorageService Access
                        private val storageService = StorageService(getSettingsFactory().createSettings())
                        
                        @Serializable
                        data class MaintenanceSettings(
                            val adminPassword: String = "123456", // Default admin password
                            val drinkStockLevels: Map<String, Int> = mapOf(), // Stock levels for drinks
                            val priceSettings: Map<String, Double> = mapOf(), // Price settings for products
                            val lastMaintenanceDate: Long = 0, // Timestamp of last maintenance
                            val isMaintenanceActive: Boolean = false // Flag to indicate if maintenance mode is active
                        )
                        
                        val settings = loadMaintenanceSettingsFromStorage()
                        // Update maintenance settings with active flag
                        val updatedSettings = settings.copy(
                            isMaintenanceActive = true,
                            lastMaintenanceDate = Clock.System.now().toEpochMilliseconds()
                        )

                        // Save to storage
                        storageService.saveObject(
                            MAINTENANCE_SETTINGS_KEY,
                            updatedSettings,
                            serializer()
                        )
                        
                        // Get from storage
                        val settings = storageService.getObject(
                            MAINTENANCE_SETTINGS_KEY,
                            serializer<MaintenanceSettings>(),
                            MaintenanceSettings()
                        ) ?: MaintenanceSettings()
                        
                        @Serializable
                        data class Transaction(
                            val timestamp: Long,
                            val drinkName: String,
                            val drinkPrice: String,
                            val amountInserted: String,
                            val changeGiven: String,
                            val coinsInserted: List<Int> = listOf(), // List of coin values in sen
                            val maintenanceAction: String = "" // Track maintenance operations
                        )

                        // Get existing transactions
                        val transactions = storageService.getObject(
                            TRANSACTIONS_KEY,
                            serializer<List<Transaction>>(),
                            listOf()
                        ) ?: listOf()

                        // Add new transaction to the list
                        val updatedTransactions = transactions + transaction

                        // Save updated transactions
                        storageService.saveObject(
                            TRANSACTIONS_KEY,
                            updatedTransactions,
                            serializer()
                        )
                        
                        //StorageService.kt
                        import com.russhwolf.settings.Settings
                        import com.russhwolf.settings.get
                        import com.russhwolf.settings.set
                        import kotlinx.serialization.KSerializer
                        import kotlinx.serialization.json.Json
                        import kotlinx.serialization.serializer
                        import kotlin.reflect.KClass

                        /**
                         * A service for handling persistent storage using multiplatform-settings
                         * This provides type-safe access to settings with JSON serialization support
                         */
                        class StorageService(private val settings: Settings) {

                            private val json = Json {
                                ignoreUnknownKeys = true
                                isLenient = true
                            }

                            /**
                             * Save an object to storage, serializing it to JSON
                             * @param key The key to store the object under
                             * @param value The object to store
                             * @param serializer The serializer for the object type
                             */
                            fun <T> saveObject(key: String, value: T, serializer: KSerializer<T>) {
                                val jsonString = json.encodeToString(serializer, value)
                                settings[key] = jsonString
                            }

                            /**
                             * Retrieve an object from storage, deserializing it from JSON
                             * @param key The key the object is stored under
                             * @param serializer The serializer for the object type
                             * @param defaultValue Optional default value if the key doesn't exist
                             * @return The deserialized object or the default value
                             * @serializer serializer<Map<Int, Int>>(), serializer<List<Transaction>>()
                             */
                            fun <T> getObject(key: String, serializer: KSerializer<T>, defaultValue: T? = null): T? {
                                val jsonString = settings.getStringOrNull(key) ?: return defaultValue
                                return try {
                                    json.decodeFromString(serializer, jsonString)
                                } catch (e: Exception) {
                                    defaultValue
                                }
                            }

                            /**
                             * Save a simple value (String, Int, Long, Float, Double, Boolean)
                             */
                            fun saveValue(key: String, value: Any) {
                                when(value) {
                                    is String -> settings[key] = value
                                    is Int -> settings[key] = value
                                    is Long -> settings[key] = value
                                    is Float -> settings[key] = value
                                    is Double -> settings[key] = value
                                    is Boolean -> settings[key] = value
                                    else -> throw IllegalArgumentException("Unsupported type: ${'$'}{value::class}")
                                }
                            }

                            /**
                             * Get a String value
                             */
                            fun getString(key: String, defaultValue: String = ""): String {
                                return settings.getStringOrNull(key) ?: defaultValue
                            }

                            /**
                             * Get an Int value
                             */
                            fun getInt(key: String, defaultValue: Int = 0): Int {
                                return settings.getIntOrNull(key) ?: defaultValue
                            }

                            /**
                             * Get a Boolean value
                             */
                            fun getBoolean(key: String, defaultValue: Boolean = false): Boolean {
                                return settings.getBooleanOrNull(key) ?: defaultValue
                            }

                            /**
                             * Check if a key exists in storage
                             */
                            fun hasKey(key: String): Boolean {
                                return settings.hasKey(key)
                            }

                            /**
                             * Remove a key from storage
                             */
                            fun remove(key: String) {
                                settings.remove(key)
                            }

                            /**
                             * Clear all stored data
                             */
                            fun clear() {
                                settings.clear()
                            }
                        }
                        
                        //androidMain
                        import android.content.Context
                        import android.content.SharedPreferences
                        import com.russhwolf.settings.Settings
                        import com.russhwolf.settings.SharedPreferencesSettings

                        /**
                         * Android implementation of the SettingsFactory
                         */
                        class AndroidSettingsFactory(private val context: Context) : SettingsFactory {
                            private val sharedPreferences: SharedPreferences = context.getSharedPreferences(
                                "vending_machine_preferences", Context.MODE_PRIVATE
                            )

                            override fun createSettings(): Settings = SharedPreferencesSettings(sharedPreferences)
                        }

                        // Global variable to store the application context
                        private var appContext: Context? = null

                        /**
                         * Initialize the settings factory with a context
                         * Call this from your Application class or MainActivity
                         */
                        fun initializeSettingsFactory(context: Context) {
                            appContext = context.applicationContext
                        }

                        /**
                         * Get Android-specific settings implementation
                         */
                        actual fun getSettingsFactory(): SettingsFactory {
                            // Use the stored appContext, or throw an exception if it's not initialized
                            val context = appContext ?: throw IllegalStateException(
                                "Settings not initialized. Call initializeSettingsFactory() first."
                            )
                            return AndroidSettingsFactory(context)
                        }
                        
                        /**
                         * Android Application class for VendingMachine
                         */
                        class VendingMachineApp : Application() {

                            override fun onCreate() {
                                super.onCreate()
                                // Initialize the settings factory with application context
                                initializeSettingsFactory(applicationContext)
                            }
                        }
                        
                        //desktopMain
                        import com.russhwolf.settings.PreferencesSettings
                        import com.russhwolf.settings.Settings
                        import java.util.prefs.Preferences

                        /**
                         * JVM/Desktop implementation of the SettingsFactory
                         */
                        class DesktopSettingsFactory : SettingsFactory {
                            private val preferences = Preferences.userRoot().node("org.junaed.vending_machine")

                            override fun createSettings(): Settings = PreferencesSettings(preferences)
                        }

                        /**
                         * Get desktop-specific settings implementation
                         */
                        actual fun getSettingsFactory(): SettingsFactory = DesktopSettingsFactory()

                        //iosMain
                        import com.russhwolf.settings.NSUserDefaultsSettings
                        import com.russhwolf.settings.Settings
                        import platform.Foundation.NSUserDefaults

                        /**
                         * iOS implementation of the SettingsFactory
                         */
                        class IOSSettingsFactory : SettingsFactory {
                            private val userDefaults = NSUserDefaults.standardUserDefaults

                            override fun createSettings(): Settings = NSUserDefaultsSettings(userDefaults)
                        }

                        /**
                         * Get iOS-specific settings implementation
                         */
                        actual fun getSettingsFactory(): SettingsFactory = IOSSettingsFactory()
                        
                        //WasmJs
                        import com.russhwolf.settings.Settings
                        import com.russhwolf.settings.StorageSettings

                        /**
                         * WASM implementation of the SettingsFactory that uses browser's localStorage
                         */
                        class WasmJsSettingsFactory : SettingsFactory {
                            override fun createSettings(): Settings = StorageSettings()
                        }

                        /**
                         * Get WASM-specific settings implementation
                         */
                        actual fun getSettingsFactory(): SettingsFactory = WasmJsSettingsFactory()
                    """.trimIndent()
            )

            SpanText(
                text = "State - Event",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = $$"""
                        /**
                         * Functionality to save and restore a [StateMachineSnapshot].
                         * @param stateMachineName Key to used inside [Settings].
                         * @param settings Instance of [multiplatform-settings](https://github.com/russhwolf/multiplatform-settings).
                         * @param decoder [Json] instance for json operations.
                         */
                        internal class StateMachineCache(
                            val stateMachineName: String,
                            val settings: Settings,
                            val decoder: Json = Json { ignoreUnknownKeys = true }
                        ) {

                            inline fun <reified STATE : Any, reified EVENT : Any> saveState(state: StateMachineSnapshot<STATE, EVENT>) {
                                settings.putString("${stateMachineName}_cache", decoder.encodeToString(state))
                            }

                            inline fun <reified STATE, reified EVENT> restoreState(): StateMachineSnapshot<STATE, EVENT>? {
                                val key = "${stateMachineName}_cache"
                                return if (settings.contains(key)) {
                                    try {
                                        decoder.decodeFromString(settings.getString(key, ""))
                                    } catch (e: SerializationException) {
                                        null
                                    }
                                } else null
                            }

                            fun clear() {
                                settings.remove("${stateMachineName}_cache")
                            }
                        }
                        
                        @Serializable
                        internal data class StateMachineSnapshot<STATE, EVENT>(val state: STATE, val event: EVENT)
                        
                         private val cache = StateMachineCache("Expressus", get())
                         cache.saveState(StateMachineSnapshot("", "))
                    """.trimIndent()
            )
        }
    }
}

/*
https://github.com/thementalgoose/kmp-flashback.git

interface DeviceRepository {
    var installationId: String
}

@OptIn(ExperimentalUuidApi::class)
internal class DeviceRepositoryImpl(
    private val preferenceManager: PreferenceManager,
    private val configManager: ConfigManager
): DeviceRepository {

    /**
     * Randomly generated device UDID to uniquely identify app install session
     */
    override var deviceUdid: String
        set(value) = preferenceManager.save(keyDeviceUDID, value)
        get() {
            var key = preferenceManager.getString(keyDeviceUDID, "")
            if (key.isNullOrEmpty()) {
                val newKey = Uuid.random().toHexString()
                deviceUdid = newKey
                key = newKey
            }
            return key
        }
    }
}
*/

/*
   //commonMain
   expect class Preferences(name: String? = null) {}

   actual constructor(name: String?) {
        sharedPreferences = application.getSharedPreferences(name, Context.MODE_PRIVATE)
    }

//2
actual class PrefsWorker actual constructor(
    path: String
) {
   private val sharedPreferences = AppContext.get().getSharedPreferences(
        path,
        Context.MODE_PRIVATE
    )
}

iosMain
    internal actual val sensitiveKeyAlias: String = path.resolveAlias()

    /**
     * `userDefaults` the instance used to manage locally the preferences on `iOs`
     */
    private val userDefaults = NSUserDefaults(
        suiteName = path
    )
*/

